function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import clsx from 'clsx';
import { withStyles } from 'common/util/react';
import { InsightsQueryResults, ShowClause } from 'common/report/insights/util/types';
import { ChartType, InsightsBookmarkParams } from 'common/types/reports/bookmark';
import { MetricChartClick, MetricSegment } from 'common/charts/visualization/multi-metric/types';
import { EmptyDataReact } from 'common/charts/misc/empty-data';
import { dataGroupStore } from 'common/report/stores/data-groups';
import { getInsightsAggregateCsvExportLimit, getProjectId, getWorkspaceId, isPublicMode } from 'common/report/util';
import { DataGroup } from 'common/widgets/data-group-selector/types';
import { SegmentFilterController, SegmentItem } from 'common/charts/contexts/segment-filter-controller';
import { REPORT_LIMITS, ReportName, RESULTS_OVERALL_SUM_KEY } from 'common/report/constants';
import { getFlatSegmentDataTableColumns, getFlatSegmentTableRow, getSeriesDisplaySegmentColumns, getSortConfig, isSingleSegmentedMetric } from 'common/report/insights/util';
import { ParamsAndResults } from 'common/report/types';
import { PopoverActionCallback, PopoverCallback } from 'common/charts/types';
import { ContextMenuReact } from 'common/widgets/context-menu';
import { getOffsetParent } from 'common/util/dom';
import { OTHER_SEGMENT_VALUE } from 'common/report/insights/util/constants';
import { computeMetricChartTooltipSections, computeMetricClauseByKey, MetricClauseData } from 'common/report/insights/util/tooltip';
import { ChartTooltipProps } from 'common/components/chart-tooltip';
import { renamePropertyValue } from 'common/report/util/properties';
import { isCustomCsvLimitEnabled } from 'common/widgets/csv-export-modal/utils';
import { getFinalDisplayOptions, getMetricFormattingDefaultsFromShowClause, getMetricKeyToShowClauseMap, metricFormatterFunctionForShowClause } from 'common/report/insights/charts/util';
import { CommonDataTableFields } from 'common/components/data-table/types';
import { formatMetricNumberWithUnits } from 'common/util';
import { eventPropertyDefinitionsStore } from 'common/report/stores/property-definitions';
import { ClauseType } from 'common/report/insights/models/types';
import { MultiMetricChartReact } from 'common/charts/visualization/multi-metric';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useTranslation } from 'common/i18n/hooks';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { IronContextRegistry } from 'common/report/context-registry';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import { getDefaultSelectedSegmentLimit, getMetricData, getSegmentMetricData, InsightsMetricData } from './util';
type InsightsMetricChartProps = {
  className?: string;
  /**
   * dataGroups will automatically be obtained from Stores without this prop,
   *  but this prop is available for environments where Stores aren't supported (eg. Public Dashboards)
   */
  dataGroups?: Record<string, DataGroup>;
  onPopoverAction?: PopoverActionCallback;
  /**
   * Use a single prop for bookmark params and results so they can be updated
   * together atomically.
   */
  paramsAndResults: ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults> | null;
  popoverCallback?: PopoverCallback;
  segmentFilterController: SegmentFilterController;
};

// type InsightsMetricState = InsightsMetricData;

export const InsightsMetricChart: React.FC<Pick<IronContextRegistry, `currentProject` | `i18n` | `metrics`> & InsightsMetricChartProps> = props => {
  var _bookmarkParams$liftC;
  const [chartData, setChartData] = useState<InsightsMetricData | null>(null);
  const [bookmarkParams, setBookmarkParams] = useState<InsightsBookmarkParams | null>(null);
  const [results, setResults] = useState<InsightsQueryResults | null>(null);
  const [popoverSegment, setPopoverSegment] = useState<MetricSegment | null>(null);
  const [popoverStyles, setPopoverStyles] = useState<Record<string, string> | null>(null);
  const rootRef = useRef(null);
  const localCurrentProject = isPublicMode() ? undefined : props.currentProject.get().value;
  const hasRequiredParams = (localBookmarkParams: InsightsBookmarkParams, localResults: InsightsQueryResults): boolean => {
    return Boolean(localBookmarkParams && localResults);
  };
  const formatValue = function formatValue(value: number, showClause: ShowClause): string {
    let forTooltip = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    const formattedValue = metricFormatterFunctionForShowClause(showClause)(value);
    const defaultOptions = getMetricFormattingDefaultsFromShowClause(showClause);
    return formatMetricNumberWithUnits(formattedValue, _objectSpread({}, getFinalDisplayOptions(showClause, eventPropertyDefinitionsStore, props.metrics, defaultOptions,
    // Tooltip values should never be abbreviated.
    forTooltip ? {
      abbrev: false
    } : {})));
  };
  const getDataGroups = (): Record<string, DataGroup> => {
    var _dataGroupStore$value;
    if (props.dataGroups) {
      return props.dataGroups;
    }
    return (_dataGroupStore$value = dataGroupStore.value) !== null && _dataGroupStore$value !== void 0 ? _dataGroupStore$value : {};
  };
  const updateChartData = (localBookmarkParams: InsightsBookmarkParams, localResults: InsightsQueryResults) => {
    if (hasRequiredParams(localBookmarkParams, localResults)) {
      const data = getMetricData(localResults, localBookmarkParams);
      const metricKeyToShowSection = getMetricKeyToShowClauseMap(localBookmarkParams, localResults);
      setChartData(data);
      const segments = Array.from(data.segmentLookup.values()).filter(segment => !segment.path.includes(RESULTS_OVERALL_SUM_KEY));
      const segmentColumns = getSeriesDisplaySegmentColumns(localBookmarkParams.sections, localResults, getDataGroups(), props.i18n);
      const _isSingleSegmentedMetric = isSingleSegmentedMetric(localResults.headers, localResults.series);
      const legendItems: SegmentItem[] = segments.map(segment => {
        var _data$timeComparisonL;
        return {
          id: segment.key,
          // improves readability for the legend when displaying results for single segmented metrics
          label: _isSingleSegmentedMetric ? segment.displayNamePath.slice(1).join(" / ") : segment.displayName,
          tableRow: getFlatSegmentTableRow({
            segment,
            timeComparisonSegment: (_data$timeComparisonL = data.timeComparisonLookup) === null || _data$timeComparisonL === void 0 ? void 0 : _data$timeComparisonL.get(segment.key)
          })
        };
      });

      // this.dataGroups exists only for public dashboard. In case of public dashboard, we assign null
      // to projectStore to prevent getContext() from fetching data from /metadata endpoint
      const currentProject = props.dataGroups ? null : localCurrentProject;
      const defaultNumSelections = getDefaultSelectedSegmentLimit(currentProject);
      const maxNumSelections = REPORT_LIMITS[ReportName.Insights].segments[ChartType.Metric].max;
      const {
        apiResultsTruncated,
        queryLimits
      } = localResults.meta;
      props.segmentFilterController.loadSections([{
        items: legendItems,
        metadata: data.legendIdToIndexMap,
        tableColumns: getFlatSegmentDataTableColumns({
          segmentColumns,
          hasTimeComparison: !!localResults.time_comparison,
          cellRenderer: _ref => {
            let {
              item,
              context
            } = _ref;
            const showClause = metricKeyToShowSection[item[CommonDataTableFields.SegmentPrefix + "0"]];
            return formatValue(item[context.field], showClause);
          }
        }),
        apiResultsTruncated
      }], {
        tableSortConfig: getSortConfig({
          bookmarkSortConfig: localBookmarkParams.sorting,
          headerCount: localResults.headers.length
        })[ChartType.Metric],
        defaultNumSelections,
        maxNumSelections
      });
      props.segmentFilterController.segmentationQueryLimit = queryLimits === null || queryLimits === void 0 ? void 0 : queryLimits.limit;
      props.segmentFilterController.exportSegmentationQueryLimit = isCustomCsvLimitEnabled(currentProject) ? getInsightsAggregateCsvExportLimit(currentProject) : REPORT_LIMITS[ReportName.Insights].csvExport;
    }
  };
  const updatePersistedLegendSelections = (localBookmarkParams: InsightsBookmarkParams): void => {
    var _localBookmarkParams$;
    const individualSelectedSegments = localBookmarkParams === null || localBookmarkParams === void 0 || (_localBookmarkParams$ = localBookmarkParams.legend) === null || _localBookmarkParams$ === void 0 ? void 0 : _localBookmarkParams$.individualSelectedSegments;
    if (individualSelectedSegments) {
      props.segmentFilterController.loadFromSavedLegend(individualSelectedSegments ? [individualSelectedSegments] : null);
    }
  };
  useEffect(() => {
    const {
      params: newParams,
      results: newResults
    } = props.paramsAndResults || {};
    if (!isEqual(bookmarkParams, newParams) || !isEqual(results, newResults)) {
      setBookmarkParams(newParams);
      setResults(newResults);
      updateChartData(newParams, newResults);
      updatePersistedLegendSelections(newParams);
    }
  }, [props.paramsAndResults]);
  useEffect(() => {
    updateChartData(bookmarkParams, results);
  }, [props.dataGroups]);
  useEffect(() => {
    if (!props.dataGroups && getProjectId()) {
      dataGroupStore.fetch({
        projectId: getProjectId(),
        workspaceId: getWorkspaceId()
      });
    }
    updateChartData(bookmarkParams, results);
    updatePersistedLegendSelections(bookmarkParams);
  }, []);
  const handlePopoverClose = () => {
    setPopoverSegment(null);
    setPopoverStyles(null);
  };
  const handlePopoverAction = (ev: CustomEvent) => {
    var _props$onPopoverActio;
    const {
      path,
      color
    } = popoverSegment;
    (_props$onPopoverActio = props.onPopoverAction) === null || _props$onPopoverActio === void 0 || _props$onPopoverActio.call(props, {
      segment: path,
      color,
      action: ev.detail.action
    });
    handlePopoverClose();
  };
  const renderPopover = () => {
    if (!popoverSegment || !props.popoverCallback) {
      return <></>;
    }
    const segment = popoverSegment.path[popoverSegment.path.length - 1];
    const segments = popoverSegment.path;
    return <ContextMenuReact className={styles.contextMenu} menuItems={props.popoverCallback(segment, segments)} relativePositioning open position={{
      left: 0,
      top: 0
    }} style={popoverStyles} onAction={(ev: CustomEvent) => handlePopoverAction(ev)} onClose={() => handlePopoverClose()} data-sentry-element="ContextMenuReact" data-sentry-component="renderPopover" data-sentry-source-file="index.tsx" />;
  };
  const handleClick: MetricChartClick = (ev, metricSegment) => {
    var _props$popoverCallbac;
    ev.stopPropagation();
    const {
      pageX,
      pageY
    } = ev;
    const {
      path
    } = metricSegment;

    // don't support actions for "Other"
    if (path[path.length - 1] === OTHER_SEGMENT_VALUE || !props.popoverCallback) {
      return;
    }

    // getOffsetParent could return null in some cases
    const parentRect = getOffsetParent(rootRef.current).getBoundingClientRect();
    const gap = 12;
    const left = pageX - parentRect.x - window.scrollX + gap;
    const top = pageY - parentRect.y - window.scrollY + gap;
    const segment = path[path.length - 1];
    const segments = path;
    const hasPopoverActions = (_props$popoverCallbac = props.popoverCallback) === null || _props$popoverCallbac === void 0 ? void 0 : _props$popoverCallbac.call(null, segment, segments).length;
    if (hasPopoverActions) {
      setPopoverStyles({
        left: "".concat(left, "px"),
        top: "".concat(top, "px")
      });
      setPopoverSegment(metricSegment);
    }
  };
  const getMetricClauseFromSegment = (segment: MetricSegment): MetricClauseData => {
    const metricClauseByKey = computeMetricClauseByKey({
      sectionsMetadata: results.meta.report_sections,
      bookmarkParams,
      dataGroups: Object.values(getDataGroups())
    });
    const metricKey = segment.path[0];
    return metricClauseByKey[metricKey];
  };
  const getTooltipData = (segment: MetricSegment): ChartTooltipProps => {
    const clause = getMetricClauseFromSegment(segment);
    const showClause = clause.clauseType === ClauseType.Show ? clause : null;
    return {
      titleColor: segment.color,
      sections: computeMetricChartTooltipSections({
        metricClauseData: clause,
        formatter: value => formatValue(value, showClause, true),
        segments: segment.path.slice(1).map((value, index) => {
          var _bookmarkParams$secti;
          return renamePropertyValue(value, results.headers[index + 1], (_bookmarkParams$secti = bookmarkParams.sections.group) === null || _bookmarkParams$secti === void 0 ? void 0 : _bookmarkParams$secti[index]);
        }),
        value: segment.value,
        secondaryValue: segment.secondaryValue
      })
    };
  };
  const getMetricSegments = (): MetricSegment[] => {
    return props.segmentFilterController.getSelectedItems(0).map(item => {
      return getSegmentMetricData({
        segmentFilterController: props.segmentFilterController,
        segmentLookup: chartData === null || chartData === void 0 ? void 0 : chartData.segmentLookup,
        timeComparisonLookup: chartData === null || chartData === void 0 ? void 0 : chartData.timeComparisonLookup,
        liftComparisonLookup: chartData === null || chartData === void 0 ? void 0 : chartData.liftComparisonLookup,
        segmentId: item.id,
        bookmarkParams,
        results,
        dataGroups: getDataGroups(),
        i18n: props.i18n
      });
    });
  };
  if (!hasRequiredParams(bookmarkParams, results) || !(chartData !== null && chartData !== void 0 && chartData.segmentLookup)) {
    return <div className={clsx(styles.root, props.className)} />;
  }
  if (isEmpty(results === null || results === void 0 ? void 0 : results.series)) {
    return <div className={clsx(styles.root, props.className)}>
        <EmptyDataReact />
      </div>;
  }
  return <div className={clsx(styles.root, props.className)} elref='insights-metric-chart' ref={rootRef} data-sentry-component="InsightsMetricChart" data-sentry-source-file="index.tsx">
      <MultiMetricChartReact elref={"multiMetric"} liftType={(_bookmarkParams$liftC = bookmarkParams.liftComparison) === null || _bookmarkParams$liftC === void 0 ? void 0 : _bookmarkParams$liftC.type} metrics={getMetricSegments()} selectedMetric={popoverSegment} metricFormatter={(segment, value) => {
      const clause = getMetricClauseFromSegment(segment);
      return formatValue(value, clause.clauseType === ClauseType.Show ? clause : null);
    }} onMetricChartClick={props.popoverCallback && handleClick} tooltipDataCallback={segment => getTooltipData(segment)} data-sentry-element="MultiMetricChartReact" data-sentry-source-file="index.tsx" />
      {/* render popover */}
      {renderPopover()}
    </div>;
};
export const InsightsMetricChartReact = withStyles((props: InsightsMetricChartProps) => {
  const currentProject = useCurrentProject();
  const i18n = useTranslation();
  const metrics = useMetrics();
  return <InsightsMetricChart currentProject={currentProject} i18n={i18n} metrics={metrics} {...props} />;
}, css);